/**
 * 打开唤起喜鹊系统
 * 详见 https://uponly.larksuite.com/wiki/wikusHPWtCoUcg9LrV0o5WcX47g?from=from_lark_group_search
 */
import { isObjectLike } from 'lodash-es';

import { SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE } from '@/utils/constants/sub-account';
import XSensors from '@/utils/skynet';
import { $tracing } from '@/utils/tracing';

interface ICaptchaV2Result {
  serialNo: string;
  expire_time: string;
  byPass: boolean;
  pass: boolean;
}

interface ICaptchaV2ErrorResult {
  /**
   * 10001: ui-security加载喜鹊系统失败, core里边返回的
   * 80001: order接口错误（2s超时 / 网络错误 / 返回内容错误 / 接口ret_code不为0 / 可用范围为空 / 从可用范围中找不到对应实例）
   * 80002: 命中黑名单，校验不通过
   * 80003: 用户完成行为验证，但是验证结果在后端校验失败 2s
   * 80004: 所有行为验证都不可用
   */
  code: number | string;
  msg: string;
}
interface IMagpieVerifyInfo {
  magpie_verify_info: {
    scene: string;
    token: string;
  };
}
type ShowCaptchaFn = () => void;
let captchaV2Instance: any = null;
const openOldCaptcha = (showOldCaptcha: ShowCaptchaFn) => {
  showOldCaptcha();
};
const verificationType = 'magpie';
const actualScenario = 'subAccount';

const TIMEOUT = 5000;
let timeoutId: any = 0;
export const openCaptchaV2 = (
  info: {
    captchaCallBack: (magpieVerifyInfo: IMagpieVerifyInfo) => void;
    scene: string;
    business_type: string;
    useDowngrade: boolean;
  },
  showOldCaptcha: ShowCaptchaFn,
  setCapthaV2Loading: (b: boolean) => void,
) => {
  const startTime = performance.now();
  const {
    useDowngrade,
    business_type = 'subAccount',
    captchaCallBack,
    scene = SUB_ACCOUNT_LOGIN_CAPTCHA_SCENE,
  } = info;
  if (captchaV2Instance) {
    captchaV2Instance?.dealloc();
  }

  if (window?.BybitUniFrame?.captchaV2) {
    // 唤起喜鹊的数量
    XSensors?.track('LoginPictureVerificationView', {
      business_type: business_type,
      verification_type: verificationType,
      actualScenario: actualScenario,
    });
    const onError = (err: ICaptchaV2ErrorResult) => {
      const endTime = performance.now();
      const executionTime = endTime - startTime;

      if (err?.code === '10001') {
        // 喜鹊系统 init失败上报
        $tracing?.addEvent({
          t: 'captchaV2InitError',
          ext: {
            executionTime,
          },
        });
      } else {
        // 喜鹊系统失败后上报
        $tracing?.addEvent({
          t: 'captchaV2Error',
          ext: {
            scene,
            ...(isObjectLike(err) ? err : {}),
            executionTime,
          },
        });
      }

      XSensors?.track('LoginPictureVerificationResult', {
        business_type: business_type,
        actualScenario: verificationType,
        verification_type: verificationType,
        is_success: false,
        execution_time: executionTime,
        failure_reason: isObjectLike(err)
          ? `captchaV2Error - ${err?.code}: ${err?.msg}`
          : 'captchaV2Error',
      });
      openOldCaptcha(showOldCaptcha);
    };
    const onSuccess = (res: ICaptchaV2Result) => {
      const endTime = performance.now();
      const executionTime = endTime - startTime;
      try {
        stopLoading();
        timeoutId && clearTimeout(timeoutId);
        // 喜鹊成功的上报
        XSensors?.track('LoginPictureVerificationResult', {
          business_type: business_type,
          actualScenario: actualScenario,
          verification_type: verificationType,
          is_success: true,
          execution_time: executionTime,
          failure_reason: '',
        });
        captchaCallBack({
          magpie_verify_info: {
            token: res?.serialNo,
            scene,
          },
        });
      } catch (err: any) {
        // 喜鹊成功回调失败的上报, 大概率不会进来
        $tracing?.addEvent({
          t: 'captchaV2SuccessFnError',
          ext: {
            msg: err?.toString(),
            executionTime,
          },
        });
        XSensors?.track('LoginPictureVerificationResult', {
          business_type: business_type,
          actualScenario: actualScenario,
          verification_type: verificationType,
          is_success: false,
          failure_reason: isObjectLike(err)
            ? JSON.stringify(err)
            : 'captchaV2SuccessFnError',
          execution_time: executionTime,
        });
        openOldCaptcha(showOldCaptcha);
      }
    };
    const stopLoading = () => {
      setCapthaV2Loading(false);
    };
    window?.BybitUniFrame?.ready(({ captchaV2 }: { captchaV2: any }) => {
      captchaV2
        .init()
        .then((captchaV2Item: any) => {
          setCapthaV2Loading(true);
          timeoutId && clearTimeout(timeoutId);
          timeoutId = setTimeout(stopLoading, TIMEOUT);
          captchaV2Instance = captchaV2Item;
          const { PARAMS, EVENT_NAME } = captchaV2Item.properties;
          const { SUCCESS, ERROR, SHOW } = captchaV2Item.events;

          captchaV2
            .setProperty(PARAMS, {
              scene, // 必填，场景码
              lang: 'en', // 非必填，语言，默认读取浏览器语言
              immediate: true, // 非必填，是否立即显示，默认为 false
              txid: '', // 非必填，设备指纹
              login_name: '', // 登陆态必填，非登陆态可选
              country_code: '', // 非必填
              showErrorToast: !useDowngrade,
            })
            .subscribeEvent(ERROR, onError)
            .subscribeEvent(SUCCESS, onSuccess)
            ?.subscribeEvent(SHOW, () => {
              timeoutId && clearTimeout(timeoutId);
              setCapthaV2Loading(false);
            });
          // 手动触发方法
          captchaV2Item.setProperty(EVENT_NAME, 'triggerClick');
        })
        .catch(() => {
          // 喜鹊系统 init失败上报
          $tracing?.addEvent({
            t: 'captchaV2InitError',
          });
          XSensors?.track('LoginPictureVerificationResult', {
            business_type: business_type,
            actualScenario: actualScenario,
            verification_type: verificationType,
            is_success: false,
            failure_reason: 'captchaV2InitError',
          });
          openOldCaptcha(showOldCaptcha);
        });
    });
  } else {
    $tracing?.addEvent({
      t: 'captchaV2LoadError',
    });
    XSensors?.track('LoginPictureVerificationResult', {
      business_type: business_type,
      actualScenario: actualScenario,
      verification_type: verificationType,
      is_success: false,
      failure_reason: 'captchaV2LoadError',
    });
    // uniframe失败的话，展示老版本uniframe
    openOldCaptcha(showOldCaptcha);
  }
};
